import { useState, useEffect, useRef } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../hooks/useAuth'

const POINTS_PER_SCAN = 5
const SPOT_CHECK_PROBABILITY = 0.3
const DEMO_MODE = true

function getDistance(lat1, lng1, lat2, lng2) {
  const R = 6371000
  const dLat = (lat2-lat1)*Math.PI/180
  const dLng = (lng2-lng1)*Math.PI/180
  const a = Math.sin(dLat/2)**2+Math.cos(lat1*Math.PI/180)*Math.cos(lat2*Math.PI/180)*Math.sin(dLng/2)**2
  return R*2*Math.atan2(Math.sqrt(a),Math.sqrt(1-a))
}

// Βήματα της ροής
// 1. scan       → κάμερα για QR
// 2. geocheck   → GPS επιβεβαίωση
// 3. bagphoto   → φωτογραφία σακούλας (ΠΑΝΤΑ)
// 4. spotcheck  → φωτογραφία ανοιχτού κάδου (30% πιθανότητα)
// 5. success / error

export default function ScanPage({ onScanSuccess }) {
  const { user, profile, refreshProfile } = useAuth()
  const [step, setStep] = useState('scan')
  const [message, setMessage] = useState('')
  const [scannedBin, setScannedBin] = useState(null)
  const [geoInfo, setGeoInfo] = useState(null)
  const [bagPhoto, setBagPhoto] = useState(null)
  const [spotPhoto, setSpotPhoto] = useState(null)
  const [needsSpotCheck, setNeedsSpotCheck] = useState(false)
  const [recentScans, setRecentScans] = useState([])
  const [loading, setLoading] = useState(false)
  const [scanning, setScanning] = useState(false)
  const [countdown, setCountdown] = useState(60)
  const videoRef = useRef(null)
  const streamRef = useRef(null)
  const scanIntervalRef = useRef(null)
  const countdownRef = useRef(null)

  useEffect(() => { fetchRecentScans() }, [])

  // Countdown για spot-check
  useEffect(() => {
    if (step === 'spotcheck') {
      setCountdown(60)
      countdownRef.current = setInterval(() => {
        setCountdown(c => {
          if (c <= 1) {
            clearInterval(countdownRef.current)
            setMessage('Έληξε ο χρόνος για φωτογραφία κάδου.')
            stopCamera(); setStep('error'); return 0
          }
          return c - 1
        })
      }, 1000)
    }
    return () => clearInterval(countdownRef.current)
  }, [step])

  async function fetchRecentScans() {
    const { data } = await supabase
      .from('scans').select('*, bins(name,type)')
      .eq('user_id', user.id)
      .order('scanned_at', { ascending: false }).limit(5)
    setRecentScans(data || [])
  }

  // ── ΒΗΜΑ 1: Κάμερα για QR ──
  async function startQRCamera() {
    setScanning(true)
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' }
      })
      streamRef.current = stream
      if (videoRef.current) { videoRef.current.srcObject = stream; videoRef.current.play() }
      if (!window.jsQR) {
        await new Promise((res, rej) => {
          const s = document.createElement('script')
          s.src = 'https://cdn.jsdelivr.net/npm/jsqr@1.4.0/dist/jsQR.js'
          s.onload = res; s.onerror = rej; document.head.appendChild(s)
        })
      }
      startQRDetection()
    } catch {
      setMessage('Δεν επιτράπηκε πρόσβαση στην κάμερα.')
      setScanning(false)
    }
  }

  function startQRDetection() {
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')
    scanIntervalRef.current = setInterval(() => {
      const video = videoRef.current
      if (!video || video.readyState !== 4) return
      canvas.width = video.videoWidth; canvas.height = video.videoHeight
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height)
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height)
      const code = window.jsQR(imageData.data, imageData.width, imageData.height)
      if (code) {
        clearInterval(scanIntervalRef.current)
        stopCamera()
        handleQRResult(code.data)
      }
    }, 300)
  }

  function stopCamera() {
    clearInterval(scanIntervalRef.current)
    if (streamRef.current) { streamRef.current.getTracks().forEach(t => t.stop()); streamRef.current = null }
    setScanning(false)
  }

  // ── ΒΗΜΑ 2: Επεξεργασία QR + Geolocation ──
  async function handleQRResult(qrData) {
    setLoading(true); setStep('geocheck')

    // Εύρεση κάδου στη βάση
    const { data: binData, error } = await supabase
      .from('bins').select('*').eq('qr_code', qrData).single()
    if (error || !binData) {
      setMessage(`Άγνωστο QR: "${qrData}". Ο κάδος δεν βρέθηκε.`)
      setStep('error'); setLoading(false); return
    }
    setScannedBin(binData)

    // Daily cap
    const today = new Date().toISOString().split('T')[0]
    const { count } = await supabase.from('scans')
      .select('*', { count: 'exact', head: true })
      .eq('user_id', user.id).eq('bin_id', binData.id).gte('scanned_at', today)
    if (count >= 2) {
      setMessage('Έχεις ήδη σκανάρει αυτόν τον κάδο 2 φορές σήμερα.')
      setStep('error'); setLoading(false); return
    }

    // Geolocation
    try {
      const pos = await new Promise((resolve, reject) =>
        navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 10000, enableHighAccuracy: true })
      )
      const dist = Math.round(getDistance(pos.coords.latitude, pos.coords.longitude, binData.lat, binData.lng))
      setGeoInfo({ dist })
      if (!DEMO_MODE && dist > 50) {
        setMessage(`Βρίσκεσαι ${dist}μ μακριά από τον κάδο. Πλησίασε εντός 50μ.`)
        setStep('error'); setLoading(false); return
      }
    } catch {
      setGeoInfo({ denied: true })
    }

    // Αποφασίζουμε αν θα χρειαστεί spot-check
    setNeedsSpotCheck(Math.random() < SPOT_CHECK_PROBABILITY)
    setLoading(false)

    // Πάμε στη φωτογραφία σακούλας (ΠΑΝΤΑ)
    setStep('bagphoto')
  }

  // ── ΒΗΜΑ 3: Φωτογραφία σακούλας ──
  async function handleBagPhoto(e) {
    const file = e.target.files[0]
    if (!file) return
    // Έλεγχος ότι η φωτογραφία τραβήχτηκε τώρα (live camera)
    const diffSeconds = (Date.now() - file.lastModified) / 1000
    if (diffSeconds > 30) {
      setMessage('Χρησιμοποίησε την κάμερα ζωντανά — δεν επιτρέπεται upload από gallery.')
      setStep('error'); return
    }
    setBagPhoto(URL.createObjectURL(file))
  }

  async function submitBagPhoto() {
    // Αν χρειάζεται spot-check → επόμενο βήμα
    if (needsSpotCheck) {
      setStep('spotcheck'); return
    }
    // Αλλιώς → καταγραφή απευθείας
    await recordScan(false, null)
  }

  // ── ΒΗΜΑ 4 (προαιρετικό): Spot-check φωτογραφία ανοιχτού κάδου ──
  async function handleSpotPhoto(e) {
    const file = e.target.files[0]
    if (!file) return
    const diffSeconds = (Date.now() - file.lastModified) / 1000
    if (diffSeconds > 30) {
      setMessage('Χρησιμοποίησε την κάμερα ζωντανά.')
      setStep('error'); return
    }
    setSpotPhoto(URL.createObjectURL(file))
  }

  async function submitSpotCheck() {
    clearInterval(countdownRef.current)
    await recordScan(true, true)
  }

  // ── Καταγραφή scan στη βάση ──
  async function recordScan(spotCheckRequired, spotCheckPassed) {
    setLoading(true)
    const { error } = await supabase.from('scans').insert({
      user_id: user.id,
      bin_id: scannedBin.id,
      points_earned: POINTS_PER_SCAN,
      spot_check_required: spotCheckRequired,
      spot_check_passed: spotCheckPassed,
    })
    if (error) { setMessage('Σφάλμα καταγραφής.'); setStep('error'); setLoading(false); return }
    await supabase.from('profiles').update({
      points: (profile?.points || 0) + POINTS_PER_SCAN,
      total_scans: (profile?.total_scans || 0) + 1
    }).eq('id', user.id)
    await refreshProfile()
    await fetchRecentScans()
    setStep('success'); setLoading(false)
    if (onScanSuccess) onScanSuccess()
  }

  function reset() {
    stopCamera(); clearInterval(countdownRef.current)
    setStep('scan'); setScannedBin(null); setBagPhoto(null)
    setSpotPhoto(null); setGeoInfo(null); setMessage(''); setNeedsSpotCheck(false)
  }

  const typeEmoji = { paper: '📄', plastic: '🧴', glass: '🍾', electronics: '📱', clothes: '👕', mixed: '♻️' }

  return (
    <div style={s.page}>

      {/* ── ΒΗΜΑ 1: QR SCAN ── */}
      {step === 'scan' && (
        <>
          <div style={s.card}>
            {!scanning ? (
              <div style={{ textAlign: 'center' }}>
                <span style={{ fontSize: 64 }}>📱</span>
                <h2 style={s.cardTitle}>Σκανάρισε QR Κάδου</h2>
                <p style={s.cardSub}>Άνοιξε την κάμερα και σκανάρισε το QR code που βρίσκεται κολλημένο στον κάδο</p>
                <div style={s.flowSteps}>
                  <div style={s.flowStep}><span>1️⃣</span><span>Σκανάρισε QR</span></div>
                  <div style={s.flowArrow}>→</div>
                  <div style={s.flowStep}><span>2️⃣</span><span>GPS check</span></div>
                  <div style={s.flowArrow}>→</div>
                  <div style={s.flowStep}><span>3️⃣</span><span>Φωτογράφισε σακούλα</span></div>
                  <div style={s.flowArrow}>→</div>
                  <div style={s.flowStep}><span>🎉</span><span>+5 πόντοι</span></div>
                </div>
                <button style={s.mainBtn} onClick={startQRCamera}>📷 Άνοιγμα Κάμερας</button>
              </div>
            ) : (
              <div>
                <div style={s.cameraWrap}>
                  <video ref={videoRef} style={s.video} playsInline muted />
                  <div style={s.scanOverlay}>
                    <div style={s.scanFrame} />
                  </div>
                  <p style={s.scanHint}>Στόχευσε το QR code του κάδου</p>
                </div>
                <button style={s.cancelBtn} onClick={stopCamera}>✕ Ακύρωση</button>
              </div>
            )}
          </div>

          {recentScans.length > 0 && (
            <div style={s.card}>
              <h3 style={s.sectionTitle}>Πρόσφατη δραστηριότητα</h3>
              {recentScans.map(scan => (
                <div key={scan.id} style={s.recentItem}>
                  <span style={{ fontSize: 20 }}>{typeEmoji[scan.bins?.type] || '♻️'}</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 14, fontWeight: 500, color: '#333' }}>{scan.bins?.name || 'Κάδος'}</div>
                    <div style={{ fontSize: 12, color: '#999' }}>{new Date(scan.scanned_at).toLocaleString('el-GR')}</div>
                  </div>
                  <div style={{ color: '#1A6B3C', fontWeight: 600, fontSize: 14 }}>+{scan.points_earned} πτ</div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* ── ΒΗΜΑ 2: GEOCHECK ── */}
      {step === 'geocheck' && (
        <div style={s.card}>
          <div style={s.loadingBox}>
            <div style={s.spinner} />
            <p style={{ color: '#1A6B3C', fontWeight: 600, margin: '12px 0 4px' }}>📍 Επαλήθευση τοποθεσίας...</p>
            {scannedBin && <p style={{ color: '#888', fontSize: 13, margin: 0 }}>Κάδος: {scannedBin.name}</p>}
          </div>
        </div>
      )}

      {/* ── ΒΗΜΑ 3: ΦΩΤΟΓΡΑΦΙΑ ΣΑΚΟΥΛΑΣ ── */}
      {step === 'bagphoto' && (
        <div style={s.card}>
          <div style={{ textAlign: 'center', marginBottom: 16 }}>
            <span style={{ fontSize: 48 }}>🛍️</span>
            <h2 style={s.cardTitle}>Φωτογράφισε τη σακούλα</h2>
            {scannedBin && (
              <div style={s.binBadge}>
                {typeEmoji[scannedBin.type]} {scannedBin.name}
                {geoInfo?.dist && <span style={{ color: '#888', fontSize: 12 }}> · {geoInfo.dist}μ</span>}
              </div>
            )}
            <p style={s.cardSub}>Τράβηξε φωτογραφία της σακούλας με τα ανακυκλώσιμα <b>εκείνη τη στιγμή</b>, πριν την πετάξεις στον κάδο.</p>
          </div>

          <div style={s.infoBox}>
            <span>⚠️</span>
            <span>Χρησιμοποίησε <b>μόνο την κάμερα</b> — δεν επιτρέπεται επιλογή από gallery</span>
          </div>

          {!bagPhoto ? (
            <label style={s.mainBtn}>
              📷 Τράβηξε φωτογραφία σακούλας
              <input
                type="file"
                accept="image/*"
                capture="environment"
                onChange={handleBagPhoto}
                style={{ display: 'none' }}
              />
            </label>
          ) : (
            <>
              <img src={bagPhoto} alt="bag" style={s.photoPreview} />
              <div style={s.photoOk}>✓ Φωτογραφία ελήφθη</div>
              <button style={s.mainBtn} onClick={submitBagPhoto} disabled={loading}>
                {loading ? 'Επεξεργασία...' : needsSpotCheck ? 'Επόμενο →' : `✓ Καταχώρηση (+${POINTS_PER_SCAN} πόντοι)`}
              </button>
            </>
          )}
        </div>
      )}

      {/* ── ΒΗΜΑ 4: SPOT-CHECK (30% πιθανότητα) ── */}
      {step === 'spotcheck' && (
        <div style={s.card}>
          <div style={{ textAlign: 'center', marginBottom: 16 }}>
            <span style={{ fontSize: 48 }}>🔍</span>
            <h2 style={{ color: '#1A6B3C', margin: '8px 0 4px' }}>Τυχαίος έλεγχος!</h2>
            <p style={s.cardSub}>
              Τράβηξε επιπλέον φωτογραφία του <b>ανοιχτού κάδου</b> για να επιβεβαιώσουμε ότι έριξες τα ανακυκλώσιμα.
            </p>
          </div>

          <div style={s.countdownRow}>
            <span style={{ fontSize: 14, fontWeight: 600 }}>⏱️ {countdown}δλ</span>
            <div style={{ flex: 1, height: 6, background: '#f0f0f0', borderRadius: 3, marginLeft: 10, overflow: 'hidden' }}>
              <div style={{ height: '100%', background: countdown > 15 ? '#1A6B3C' : '#dc2626', width: `${(countdown / 60) * 100}%`, transition: 'width 1s linear', borderRadius: 3 }} />
            </div>
          </div>

          <div style={s.infoBox}>
            <span>📷</span>
            <span>Άνοιξε τον κάδο και φωτογράφισε το εσωτερικό — live κάμερα μόνο</span>
          </div>

          {!spotPhoto ? (
            <label style={s.mainBtn}>
              📷 Φωτογραφία ανοιχτού κάδου
              <input
                type="file"
                accept="image/*"
                capture="environment"
                onChange={handleSpotPhoto}
                style={{ display: 'none' }}
              />
            </label>
          ) : (
            <>
              <img src={spotPhoto} alt="bin" style={s.photoPreview} />
              <div style={s.photoOk}>✓ Φωτογραφία κάδου ελήφθη</div>
              <button style={s.mainBtn} onClick={submitSpotCheck} disabled={loading}>
                {loading ? 'Επεξεργασία...' : `✓ Ολοκλήρωση (+${POINTS_PER_SCAN} πόντοι)`}
              </button>
            </>
          )}
        </div>
      )}

      {/* ── SUCCESS ── */}
      {step === 'success' && (
        <div style={s.card}>
          <div style={{ textAlign: 'center' }}>
            <span style={{ fontSize: 64 }}>🎉</span>
            <h2 style={{ color: '#1A6B3C', margin: '12px 0 8px' }}>+{POINTS_PER_SCAN} Πόντοι!</h2>
            {scannedBin && (
              <p style={{ color: '#555', fontSize: 14, margin: '0 0 4px' }}>
                {typeEmoji[scannedBin.type]} {scannedBin.name}
              </p>
            )}
            {geoInfo?.dist && (
              <p style={{ fontSize: 13, color: '#aaa', margin: '0 0 12px' }}>📍 {geoInfo.dist}μ από τον κάδο</p>
            )}
            <div style={s.successBox}>
              Σύνολο: <strong>{profile?.points || 0} πόντοι</strong>
            </div>
            <button style={s.mainBtn} onClick={reset}>📱 Νέο σκανάρισμα</button>
          </div>
        </div>
      )}

      {/* ── ERROR ── */}
      {step === 'error' && (
        <div style={s.card}>
          <div style={{ textAlign: 'center' }}>
            <span style={{ fontSize: 48 }}>❌</span>
            <h2 style={{ color: '#dc2626', margin: '12px 0 8px' }}>Αποτυχία</h2>
            <p style={s.cardSub}>{message}</p>
            <button style={s.mainBtn} onClick={reset}>Δοκίμασε ξανά</button>
          </div>
        </div>
      )}
    </div>
  )
}

const s = {
  page: { padding: '1rem', maxWidth: 480, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '1rem' },
  card: { background: 'white', borderRadius: 16, padding: '1.25rem', boxShadow: '0 2px 12px rgba(0,0,0,0.06)' },
  cardTitle: { textAlign: 'center', margin: '8px 0 6px', color: '#1A6B3C', fontSize: 20 },
  cardSub: { textAlign: 'center', color: '#666', fontSize: 14, margin: '0 0 16px', lineHeight: 1.5 },
  sectionTitle: { margin: '0 0 12px', fontSize: 15, fontWeight: 600, color: '#333' },
  flowSteps: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, margin: '12px 0 16px', flexWrap: 'wrap' },
  flowStep: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, fontSize: 11, color: '#555', background: '#f0fdf4', padding: '6px 8px', borderRadius: 8 },
  flowArrow: { color: '#ccc', fontSize: 16 },
  mainBtn: { display: 'block', width: '100%', padding: '14px', background: '#1A6B3C', color: 'white', border: 'none', borderRadius: 10, fontSize: 15, fontWeight: 600, cursor: 'pointer', textAlign: 'center', marginTop: 4 },
  cancelBtn: { width: '100%', padding: '10px', background: '#f5f5f5', color: '#666', border: 'none', borderRadius: 10, fontSize: 14, cursor: 'pointer', marginTop: 8 },
  cameraWrap: { position: 'relative', borderRadius: 12, overflow: 'hidden', background: '#000', aspectRatio: '4/3' },
  video: { width: '100%', height: '100%', objectFit: 'cover', display: 'block' },
  scanOverlay: { position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  scanFrame: { width: 180, height: 180, border: '3px solid #4CAF50', borderRadius: 12, boxShadow: '0 0 0 9999px rgba(0,0,0,0.45)' },
  scanHint: { position: 'absolute', bottom: 12, left: 0, right: 0, textAlign: 'center', color: 'white', fontSize: 13, margin: 0, textShadow: '0 1px 3px rgba(0,0,0,0.8)' },
  binBadge: { background: '#f0fdf4', border: '1px solid #c6f6d5', borderRadius: 20, padding: '6px 14px', fontSize: 13, fontWeight: 600, color: '#1A6B3C', display: 'inline-block', margin: '0 0 12px' },
  infoBox: { display: 'flex', gap: 8, alignItems: 'flex-start', background: '#fff8e1', padding: '10px 12px', borderRadius: 8, fontSize: 13, color: '#854F0B', marginBottom: 14 },
  photoPreview: { width: '100%', borderRadius: 10, marginBottom: 8, maxHeight: 180, objectFit: 'cover' },
  photoOk: { textAlign: 'center', color: '#1A6B3C', fontSize: 13, fontWeight: 600, marginBottom: 8 },
  recentItem: { display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0', borderBottom: '1px solid #f5f5f5' },
  loadingBox: { textAlign: 'center', padding: '2rem 0' },
  spinner: { width: 40, height: 40, border: '3px solid #e8f5e9', borderTop: '3px solid #1A6B3C', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto' },
  countdownRow: { display: 'flex', alignItems: 'center', background: '#f8f8f8', padding: '10px 12px', borderRadius: 8, marginBottom: 12 },
  successBox: { background: '#f0fdf4', padding: '12px', borderRadius: 8, margin: '0 0 16px', fontSize: 15 },
}
